import { Users, Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { MetricTile } from "@/components/ui/MetricTile";

export default function CoachDashboard() {
  const navigate = useNavigate();

  // Mock data for now
  const metrics = {
    kidsToday: 18,
    sessionsToday: 4,
    pendingAttendance: 2,
  };

  const teams = [
    {
      id: 1,
      name: "U10 – Beginners",
      ageGroup: "8-10 years",
      kidsCount: 12,
      nextSession: "Tomorrow, 4:00 PM",
    },
    {
      id: 2,
      name: "U12 – Intermediate",
      ageGroup: "11-12 years",
      kidsCount: 15,
      nextSession: "Today, 5:30 PM",
    },
    {
      id: 3,
      name: "U14 – Advanced",
      ageGroup: "13-14 years",
      kidsCount: 10,
      nextSession: "Wednesday, 3:00 PM",
    },
  ];

  return (
    <div className="flex-1 h-full overflow-y-auto overflow-x-hidden bg-slate-50" data-testid="coach-dashboard-page">
      <div className="max-w-5xl mx-auto px-4 py-4 md:px-8 md:py-6 space-y-4 md:space-y-6">
        {/* Page Header */}
        <div className="space-y-1">
          <h1 className="text-xl md:text-2xl font-semibold text-slate-900">
            Coach Dashboard
          </h1>
          <p className="text-sm md:text-base text-slate-500">
            Quick view of today's sessions, kids, and teams
          </p>
        </div>

        {/* Today at a Glance - Metric Tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-testid="coach-metrics">
          <MetricTile
            label="Kids Training Today"
            value={metrics.kidsToday}
            variant="info"
            testId="coach-metric-kids-today"
          />
          <MetricTile
            label="Sessions Today"
            value={metrics.sessionsToday}
            variant="success"
            testId="coach-metric-sessions-today"
          />
          <MetricTile
            label="Pending Attendance"
            value={metrics.pendingAttendance}
            variant="warning"
            testId="coach-metric-pending-attendance"
          />
        </div>

        {/* Teams Overview Preview */}
        <div className="space-y-3 md:space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg md:text-xl font-bold text-slate-900">Teams Overview</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/coach/teams")}
              className="text-xs md:text-sm font-medium"
            >
              View All Teams
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coach-teams-preview">
            {teams.map((team) => (
              <div
                key={team.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-5 hover:shadow-md transition-shadow cursor-pointer"
                data-testid="coach-team-card"
                onClick={() => navigate("/coach/teams")}
              >
                <div className="space-y-3">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-slate-900">{team.name}</h3>
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {team.ageGroup}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Kids</span>
                      <span className="font-semibold text-slate-900">{team.kidsCount}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs">{team.nextSession}</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs md:text-sm font-medium"
                  >
                    View Team
                    <ArrowRight className="h-3 w-3 md:h-4 md:w-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
